import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronDown, Shield, Download, CreditCard, Users, Clock, Phone } from 'lucide-react';

const FAQSection = () => {
  const [openFAQ, setOpenFAQ] = useState<number | null>(0); // First FAQ open by default

  const faqs = [
    {
      icon: Users,
      question: "Who owns the videos?",
      answer: "You do! HiveShare is simply a secure hosting platform. You maintain complete ownership of all your video content. If you cancel, we provide 30 days to download your videos before they're permanently deleted for privacy."
    },
    {
      icon: Download,
      question: "Can parents download videos?",
      answer: "Yes, if you allow it! You have complete control over download permissions. You can enable/disable downloads for specific videos or families. Parents can stream videos anytime, and download when permitted."
    },
    {
      icon: Shield,
      question: "How secure is HiveShare?",
      answer: "Extremely secure. We use Cloudflare's enterprise-grade CDN and security infrastructure - the same technology that protects major websites. All videos are private by default, with invite-only access and family-specific privacy controls."
    },
    {
      icon: Clock,
      question: "What happens if I cancel?",
      answer: "No problem! Your videos are stored safely for 30 days after cancellation, giving you time to download everything. After 30 days, all content is permanently deleted for privacy. No hidden fees, no hassle."
    },
    {
      icon: CreditCard,
      question: "Are there any setup fees or contracts?",
      answer: "None! No setup fees, no contracts, no hidden costs. Just $29/month (early bird pricing) for unlimited video sharing. Cancel anytime with no penalties. Your first trial is completely free - no credit card required."
    },
    {
      icon: Phone,
      question: "Do you provide support?",
      answer: "Absolutely! We provide phone and email support to help you get started and answer any questions. Most customers are up and running in under 5 minutes, but we're here if you need us."
    },
    {
      icon: Users,
      question: "How many families can I add?",
      answer: "Unlimited! Add as many families as you need. Each family gets their own secure access to only their children's videos. Perfect for studios with 10 families or 1000+ families."
    },
    {
      icon: Download,
      question: "What video formats do you support?",
      answer: "We support all common video formats (MP4, MOV, AVI, etc.). Videos are automatically optimized for streaming on any device - phones, tablets, computers. No technical knowledge required."
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Everything you need to know about HiveShare. 
            <span className="font-semibold text-gray-800"> Still have questions? We're here to help.</span>
          </p>
        </motion.div>

        <div className="max-w-4xl mx-auto">
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
              >
                <Card className={`cursor-pointer transition-all duration-300 hover:shadow-lg ${
                  openFAQ === index ? 'border-2 border-blue-200 shadow-lg' : 'border border-gray-200'
                }`}>
                  <CardContent className="p-0">
                    <button
                      onClick={() => toggleFAQ(index)}
                      className="w-full p-6 text-left flex items-center justify-between hover:bg-gray-50 transition-colors duration-200"
                    >
                      <div className="flex items-center space-x-4">
                        <div className={`p-2 rounded-lg ${
                          openFAQ === index ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'
                        }`}>
                          <faq.icon className="h-5 w-5" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {faq.question}
                        </h3>
                      </div>
                      <ChevronDown 
                        className={`h-5 w-5 text-gray-500 transition-transform duration-300 ${
                          openFAQ === index ? 'rotate-180' : ''
                        }`}
                      />
                    </button>
                    
                    <AnimatePresence>
                      {openFAQ === index && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          className="overflow-hidden"
                        >
                          <div className="px-6 pb-6">
                            <div className="pl-12">
                              <p className="text-gray-700 leading-relaxed">
                                {faq.answer}
                              </p>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Contact Support Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="mt-16 text-center"
        >
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-2xl p-8 max-w-2xl mx-auto border border-blue-200">
            <Phone className="h-8 w-8 text-blue-600 mx-auto mb-4" />
            <h3 className="text-xl font-bold text-blue-900 mb-2">
              Still Have Questions?
            </h3>
            <p className="text-blue-700 mb-4">
              Our team is here to help you get started with HiveShare. 
              Get answers in minutes, not days.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="mailto:support@hivetivity.com"
                className="inline-flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors duration-200"
              >
                Email Support
              </a>
              <a 
                href="tel:+1-555-0123"
                className="inline-flex items-center justify-center bg-white hover:bg-gray-50 text-blue-600 border-2 border-blue-600 px-6 py-3 rounded-lg font-semibold transition-colors duration-200"
              >
                <Phone className="h-4 w-4 mr-2" />
                Call Us
              </a>
            </div>
          </div>
        </motion.div>

        {/* Quick Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="mt-16"
        >
          <div className="grid md:grid-cols-3 gap-8 text-center max-w-3xl mx-auto">
            <div>
              <div className="text-3xl font-bold text-blue-600 mb-2">&lt; 5 min</div>
              <div className="text-gray-600">Average setup time</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-green-600 mb-2">99.9%</div>
              <div className="text-gray-600">Uptime guarantee</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-purple-600 mb-2">24/7</div>
              <div className="text-gray-600">Video availability</div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default FAQSection;